import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, formatTanggal } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FilePlus2, FileText, Check, X, Trash2, Filter } from "lucide-react";

const STATUS_BADGE = {
  menunggu: "bg-amber-100 text-amber-800",
  disetujui: "bg-emerald-100 text-emerald-800",
  ditolak: "bg-rose-100 text-rose-800",
};

const AVATARS = ["bg-sky-100 text-sky-700","bg-amber-100 text-amber-700","bg-emerald-100 text-emerald-700","bg-rose-100 text-rose-700","bg-teal-100 text-teal-700","bg-indigo-100 text-indigo-700"];
function avatarColor(s) { let h=0; for (let i=0;i<(s||"").length;i++) h=(h*31+s.charCodeAt(i))%9973; return AVATARS[h%AVATARS.length]; }
function initials(s) { return (s||"?").slice(0,2).toUpperCase(); }

export default function IzinPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState({ tanggal: new Date().toISOString().slice(0, 10), reason: "" });
  const [decision, setDecision] = useState(null);
  const [catatan, setCatatan] = useState("");

  const load = useCallback(async () => {
    try {
      const params = status !== "all" ? { status } : {};
      const { data } = await api.get("/izin", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  async function submitIzin() {
    if (!form.reason.trim()) return toast.error("Alasan wajib diisi");
    try {
      await api.post("/izin", form);
      toast.success("Permohonan izin terkirim");
      setOpenForm(false);
      setForm({ tanggal: new Date().toISOString().slice(0, 10), reason: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function decide(id, st) {
    try {
      await api.patch(`/izin/${id}`, { status: st, catatan: catatan || null });
      toast.success(`Izin ${st}`);
      setDecision(null);
      setCatatan("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function cancel(id) {
    try {
      await api.delete(`/izin/${id}`);
      toast.success("Izin dibatalkan");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="space-y-6" data-testid="izin-page">
      <PageHeader
        overline={isAdmin ? "Administrasi" : "Karyawan"}
        title="Izin"
        description={isAdmin ? "Permohonan izin dari seluruh karyawan." : "Ajukan dan lihat status permohonan izin Anda."}
        icon={FileText}
        tone="invoice"
        actions={
          !isAdmin && (
            <Button onClick={() => setOpenForm(true)} className="bg-brand text-white hover:bg-brand-600" data-testid="btn-create-izin">
              <FilePlus2 className="mr-2 h-4 w-4" /> Ajukan Izin
            </Button>
          )
        }
      />

      <Card className="p-4 border-border shadow-none">
        <div className="flex items-center gap-3 text-sm">
          <Filter className="h-4 w-4 text-ink-500" />
          <Label className="text-ink-500">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[180px]" data-testid="filter-izin-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              <SelectItem value="menunggu">Menunggu</SelectItem>
              <SelectItem value="disetujui">Disetujui</SelectItem>
              <SelectItem value="ditolak">Ditolak</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {items.length === 0 && (
          <Card className="col-span-full p-10 text-center text-sm text-ink-500 border-border shadow-none">
            Belum ada permohonan izin.
          </Card>
        )}
        {items.map((it) => (
          <Card key={it.id} className="border-border shadow-none p-4" data-testid={`izin-${it.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-full text-xs font-semibold ${avatarColor(it.user_username)}`}>
                  {initials(it.user_name)}
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-ink-900 truncate">{it.user_name}</div>
                  <div className="text-xs text-ink-500">@{it.user_username} · {formatTanggal(it.tanggal)}</div>
                </div>
              </div>
              <Badge className={`${STATUS_BADGE[it.status]} hover:${STATUS_BADGE[it.status]}`}>{it.status}</Badge>
            </div>
            <p className="mt-3 rounded-md bg-secondary/40 p-3 text-sm text-ink-700">{it.reason}</p>
            {it.catatan && (
              <p className="mt-2 text-xs text-ink-500">Catatan: <span className="text-ink-700">{it.catatan}</span></p>
            )}
            {it.decided_at && (
              <p className="mt-1 text-xs text-ink-500">Diputuskan oleh @{it.decided_by} · {formatTanggal(it.decided_at)}</p>
            )}

            <div className="mt-3 flex flex-wrap justify-end gap-2">
              {isAdmin && it.status === "menunggu" && (
                <>
                  <Button size="sm" variant="outline" onClick={() => { setDecision({ id: it.id, status: "ditolak" }); setCatatan(""); }} data-testid={`btn-reject-${it.id}`}>
                    <X className="mr-1 h-3 w-3" /> Tolak
                  </Button>
                  <Button size="sm" onClick={() => { setDecision({ id: it.id, status: "disetujui" }); setCatatan(""); }} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`btn-approve-${it.id}`}>
                    <Check className="mr-1 h-3 w-3" /> Setujui
                  </Button>
                </>
              )}
              {!isAdmin && it.status === "menunggu" && (
                <Button size="sm" variant="ghost" onClick={() => cancel(it.id)} data-testid={`btn-cancel-${it.id}`}>
                  <Trash2 className="mr-1 h-3 w-3" /> Batalkan
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Create izin */}
      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajukan Izin</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tanggal Izin</Label>
              <DatePicker value={form.tanggal} onChange={(v) => setForm({ ...form, tanggal: v })} placeholder="Pilih tanggal" testId="input-izin-tanggal" />
            </div>
            <div>
              <Label>Alasan</Label>
              <Textarea
                rows={4}
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Jelaskan alasan izin Anda…"
                data-testid="input-izin-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenForm(false)}>Batal</Button>
            <Button onClick={submitIzin} className="bg-brand text-white hover:bg-brand-600" data-testid="submit-izin">Kirim Permohonan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decide dialog (admin) */}
      <Dialog open={!!decision} onOpenChange={(v) => !v && setDecision(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {decision?.status === "disetujui" ? "Setujui Izin" : "Tolak Izin"}
            </DialogTitle>
          </DialogHeader>
          <div>
            <Label>Catatan (opsional)</Label>
            <Textarea rows={3} value={catatan} onChange={(e) => setCatatan(e.target.value)} placeholder="Tambahkan catatan untuk karyawan…" data-testid="input-catatan" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecision(null)}>Batal</Button>
            <Button
              onClick={() => decide(decision.id, decision.status)}
              className={decision?.status === "disetujui" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"}
              data-testid="confirm-decide"
            >
              {decision?.status === "disetujui" ? "Setujui" : "Tolak"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
